import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  buildChartsModel,
  buildConversationChartModel,
  emptyChartsModel,
  selectTagsAnswer
} from '@aytm/app/stats/page/question-views/oe/lib/chartsModel'
import { calcTableProps } from '@aytm/app/stats/page/question-views/oe/lib/sentiments-table-utils'

import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import Chart from '@aytm/app/stats/page/question-views/oe/components/chart/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

// Conversation's chart surface. Two of its three modes are the shared sentiments / tags
// breakdown; the third is conversation's own per-turn one, whose positions come from the
// answer's own a{n} keys rather than from a resolved tag list. Both end up as the same
// { answer, answerVariants } pair, so the chart and the items list below it stay in step.
const ChartsView = ({ components, answer, chartHeight, chartsModelProps, chartProps, tableProps }) => {
  const { kind, viewMode, answer: modelAnswer, answerVariants, config, keys, codebook } = chartsModelProps || {}

  const chartsModel = useMemo(() => {
    if ('conversation' === kind) return buildConversationChartModel({ answer: modelAnswer, answerVariants })
    if ('tags' === kind) {
      return buildChartsModel({
        viewMode,
        answer: selectTagsAnswer(viewMode, modelAnswer, config),
        keys,
        codebook
      })
    }

    return emptyChartsModel()
  }, [kind, viewMode, modelAnswer, answerVariants, config, keys, codebook])

  // the payload sentinel is the raw answer, so the view decides its own loading state
  const isLoading = 'loading' === answer

  return (
    <>
      {isComponentOn(components, 'chart') &&
        !isMapOrNone(chartProps?.questionChartMode, chartProps?.globalChartMode) && (
          <ChartPlaceholder height={chartHeight} isLoading={isLoading}>
            <Chart answer={chartsModel.answer} answerVariants={chartsModel.answerVariants} {...chartProps} />
          </ChartPlaceholder>
        )}
      {isComponentOn(components, 'table') && (
        <Table
          {...calcTableProps({
            answer: chartsModel.answer,
            answerVariants: chartsModel.answerVariants,
            ...(tableProps || {})
          })}
        />
      )}
    </>
  )
}

ChartsView.displayName = 'CnChartsView'

ChartsView.propTypes = {
  components: PropTypes.object,
  answer: answerPropType,
  chartHeight: PropTypes.number,
  chartsModelProps: PropTypes.object,
  chartProps: PropTypes.object,
  tableProps: PropTypes.object
}

export default ChartsView
